import customStore from 'caches/utils/localStorage';
import dayjs from 'dayjs';
import { EXPERIMENT_STORE } from 'experiments/utils/constants';
import logExposure from 'experiments/services/logExposure';
import jsCookies from 'js-cookie';
import { ISignal } from 'shared/hooks/useAbortController';
import { IExperiment, IExperimentGroup, IPureExperiment, IPureExperimentGroup } from './types';

/**
 * [isExperimentActive]
 * Check if an experiment is still active and not expired
 * @param   {number} endTimestamp The end time at which the experiment dies
 */
export function isExperimentActive(endTimestamp) {
  if (endTimestamp) {
    const currentTimeStamp = dayjs(new Date()).valueOf();
    return endTimestamp - currentTimeStamp >= 0;
  }

  return true;
}

/**
 * [formatExperimentsList]
 * format raw experiment list from the server
 * remove grouplist, starttime, description, owner properties
 * @param   {Array} rawExperiments the list of raw experiments objects from the server
 * @returns {Array} List of formatted experiments with only id, name & timestamp
 */

export function formatExperimentsList(
  rawExperiments: Array<IPureExperiment> = [],
): Array<IExperiment> {
  return (
    rawExperiments?.filter((experiment) => {
      // if experiment exists and name starts with web, we approve
      // TODO: Change this logic once backend allows to fetch by platform
      const isWebExperiment = experiment && experiment.name?.startsWith('web_');
      return isWebExperiment;
    }) ?? []
  ).map((experiment) => {
    const { id, name, endTimestamp } = experiment;
    return { id, name, endTimestamp: endTimestamp?.seconds };
  });
}

export function getCurrentActiveExperiments() {
  try {
    const currentActiveExperiments = jsCookies.getJSON(EXPERIMENT_STORE.ACTIVE_EXPERIMENTS) || [];

    // filter expired experiments
    return currentActiveExperiments.filter((experiment: IExperiment): boolean =>
      isExperimentActive(experiment?.endTimestamp),
    );
  } catch {
    return null;
  }
}

/**
 * [formatExperimentGroups]
 * remove unnecessary properties from raw group experiments response
 * which is coming from the server
 * @param   {Array} groupsList the list of raw experiments groups objects from the server
 * @returns {Array} List of formatted experiments groups with only id, group name & timestamp
 */

export function formatExperimentGroups(
  groupsList: Array<IPureExperimentGroup>,
): Record<string, IExperimentGroup> {
  try {
    const formattedGroups = {};

    for (const groupItem of groupsList) {
      formattedGroups[groupItem.experiment.name] = {
        id: groupItem.experiment.id,
        group: groupItem.group.name,
        endTimestamp: groupItem.experiment.endTimestamp?.seconds,
      };
    }

    return formattedGroups;
  } catch {
    return {};
  }
}

/**
 * [logExperimentExposure]
 * This function is called when the inviewport event has been fired on a [data-experiment] element
 * are there any existing experiment on the client?
 * has the current experiment we are trying to log has already been logged
 * is user is assigned a group for the experiment?
 * is the experiment is still active
 * if all of the above is YES
 * then log the exposure
 * update localstorage
 * @param   {string} deviceId The deviceId with which we are logging all experiments
 * @param   {string} experimentName Experiment name
 * @param   {boolean} isAuthenticated whether the user is authenticated or not
 * @param   {Object} optionalMetaData metadata containing userId and sessionId if available
 * @param   {Object} signal AbortSignal to prevent unnecessary calls
 */

export function logExperimentExposure({
  deviceId,
  experimentName,
  isAuthenticated,
  optionalMetaData,
  signal,
}: {
  deviceId: string;
  experimentName: string;
  isAuthenticated: boolean;
  optionalMetaData: {
    sessionId?: string;
    userId?: number;
  };
  signal: ISignal;
}): Promise<void> {
  const loggedExperiments = customStore.getItem(EXPERIMENT_STORE.LOGGED_EXPERIMENTS) || {};

  // non blocking
  return logExposure({
    deviceId,
    experimentName,
    isAuthenticated,
    optionalMetaData,
    signal,
  }).then(() => {
    //  update the current storage to reflect the exposure
    loggedExperiments[experimentName] = true;
    customStore.setItem(EXPERIMENT_STORE.LOGGED_EXPERIMENTS, loggedExperiments);
  });
}
